import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Sparkles, ShieldCheck, ShieldOff, Info } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { api } from '../utils/api';

/**
 * PrivacyAICard — Settings card for managing AI processing consent.
 *
 * Required for Apple 5.1.1(i) / 5.1.2(i) compliance. Lets the user:
 *  - See the current consent status + when it was granted
 *  - Revoke consent (which server-side blocks all AI endpoints)
 *  - Re-grant consent later
 *  - Read the exact disclosure of what data is shared and with whom
 */
const PrivacyAICard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = async () => {
    try {
      const data = await api.get('/api/ai/consent/status');
      setStatus(data);
    } catch (err) {
      if (typeof window !== 'undefined' && window.console) {
        window.console.warn('AI consent status fetch failed', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleGrant = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/ai/consent', { consent_version: '1.0' });
      await loadStatus();
      toast.success('AI features are now enabled.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/ai/consent/revoke', {});
      await loadStatus();
      toast('AI features disabled. Voice capture still works locally.', {
        duration: 6000,
      });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const granted = status?.granted;
  const grantedAt = status?.granted_at ? new Date(status.granted_at) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
    >
      <Card
        className="glass-card p-6"
        data-testid="settings-privacy-ai-card"
      >
        <div className="flex items-center gap-3 mb-6">
          <Sparkles size={24} className="text-[var(--sd-accent,#b76e79)]" />
          <h2 className="text-xl font-semibold text-[var(--sd-fg)]">
            Privacy &amp; AI
          </h2>
        </div>

        <div className="space-y-4">
          {/* Status badge */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--sd-fg)]">
                AI processing consent
              </span>
              {loading ? (
                <Badge variant="outline" className="text-xs">
                  Loading…
                </Badge>
              ) : granted ? (
                <Badge
                  className="text-xs bg-emerald-100 text-emerald-800 border-emerald-200"
                  data-testid="ai-consent-status-granted"
                >
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge
                  className="text-xs bg-amber-100 text-amber-800 border-amber-200"
                  data-testid="ai-consent-status-revoked"
                >
                  <ShieldOff className="w-3 h-3 mr-1" />
                  Disabled
                </Badge>
              )}
            </div>
            {grantedAt && granted && (
              <span className="text-xs text-[var(--sd-muted-fg)]">
                Granted {format(grantedAt, 'MMM d, yyyy')}
              </span>
            )}
          </div>

          {/* Disclosure summary */}
          <div className="rounded-lg bg-[var(--sd-surface-muted,#f9f2f3)] p-4 text-sm text-[var(--sd-fg)] border border-[var(--sd-border)]">
            <div className="flex items-start gap-2 mb-2">
              <Info className="w-4 h-4 text-[var(--sd-accent,#b76e79)] flex-shrink-0 mt-0.5" />
              <p className="font-medium">
                What Selah shares when AI is enabled
              </p>
            </div>
            <ul className="pl-6 list-disc space-y-1 text-[var(--sd-muted-fg)] text-xs">
              <li>
                Your voice recordings and reflection text are sent to{' '}
                <strong>OpenAI</strong> for transcription, Scripture matching,
                and prayer generation.
              </li>
              <li>
                OpenAI does <strong>not</strong> use this data to train their
                models. They retain it briefly for abuse monitoring, then
                delete.
              </li>
              <li>
                Selah stores only the final results (transcription, Scripture,
                prayer), encrypted at rest in your private journal.
              </li>
              <li>
                Full details:{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-[var(--sd-accent,#b76e79)]"
                >
                  Privacy Policy
                </a>
                .
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            {granted ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={submitting}
                    className="border-[var(--sd-border)] text-[var(--sd-fg)]"
                    data-testid="ai-consent-revoke-trigger"
                  >
                    <ShieldOff className="w-4 h-4 mr-1.5" />
                    Revoke AI consent
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass-card">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[var(--sd-fg)]">
                      Revoke AI consent?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-[var(--sd-muted-fg)]">
                      Voice capture will keep working, but AI features
                      (transcription, Scripture matching, and prayer
                      generation) will be disabled until you re-enable
                      consent. Your existing journal entries stay intact.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-[var(--sd-border)] text-[var(--sd-fg)]">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRevoke}
                      className="bg-[var(--sd-accent,#b76e79)] hover:bg-[var(--sd-accent-hover,#a55f6a)] text-white"
                      data-testid="ai-consent-revoke-confirm"
                    >
                      Yes, disable AI features
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button
                onClick={handleGrant}
                disabled={submitting}
                className="bg-[var(--sd-accent,#b76e79)] hover:bg-[var(--sd-accent-hover,#a55f6a)] text-white"
                data-testid="ai-consent-grant-button"
              >
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                {submitting ? 'Saving…' : 'Enable AI features'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default PrivacyAICard;
